<template lang="pug">
.list-block(v-if="listBlock.organizations.length > 0")
  // Section title
  .section-title(v-if="listBlock.title")
    h6.mb-0 {{ listBlock.title.toUpperCase() }}

  // Organizations list container
  .organizations-list-wrapper
    .organization-list-item(
      v-for="(organization, index) in listBlock.organizations"
      :key="organization.id || index"
      :class="{ 'has-divider': shouldShowDivider(index) }"
      @click="handleOrganizationClick(organization)"
    )
      // Organization avatar
      .organization-avatar-wrapper
        img(
          v-if="organization.avatar"
          :src="organization.avatar"
          :alt="organization.name"
          @error="handleImageError"
        ).organization-avatar
        img(
          v-else
          src="@/assets/images/default-profile.jpeg"
        ).organization-avatar

      // Organization info
      .organization-info
        .organization-name {{ organization.name }}
        .organization-ein(v-if="organization.einNumber || organization.benevityId")
          | EIN: {{ organization.einNumber || organization.benevityId }}

      // Chevron icon
      .chevron-icon
        img(src="@/assets/icons/chevron-right.svg" alt="View")
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useRouter } from 'vue-router';
import { ListBlock as ListBlockType } from '../types';

export default defineComponent({
  name: 'ListBlock',
  props: {
    block: {
      type: Object as PropType<ListBlockType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    // Computed
    const listBlock = computed(() => props.block as ListBlockType);

    // Methods
    const handleOrganizationClick = (organization: any) => {
      if (!organization.id) return;

      // Navigate to organization public profile
      // Use slug if available, otherwise use id
      if (organization.slug) {
        router.push(`/web/org/${organization.slug}`);
      } else {
        router.push(`/web/profile/organization/${organization.id}`);
      }
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      // Fallback to default profile image
      target.src = '/src/assets/images/default-profile.jpeg';
      console.error('Failed to load organization avatar:', target.src);
    };

    const shouldShowDivider = (index: number): boolean => {
      const totalOrgs = listBlock.value.organizations.length;
      // On mobile: show divider for all except last
      // On desktop (2 columns): show divider except for last item in each column
      return index < totalOrgs - 1;
    };

    return {
      listBlock,
      handleOrganizationClick,
      handleImageError,
      shouldShowDivider,
    };
  },
});
</script>

<style lang="scss" scoped>
.list-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.section-title {
  margin-bottom: 24px;

  h6 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #1a1a1a;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}

.organizations-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  @media (min-width: 1024px) {
    gap: 20px;
  }
}

.organization-list-item {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  min-width: 0;

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }

  &:only-child {
    border-radius: 12px;
  }

  & + & {
    border-top: none;
  }

  @media (min-width: 768px) {
    border-radius: 12px !important;
    padding: 16px;

    & + & {
      border-top: 1px solid #e5e7eb;
    }

    &:first-child,
    &:last-child,
    &:only-child {
      border-radius: 12px !important;
    }
  }

  @media (min-width: 1024px) {
    padding: 20px;
  }

  &:hover {
    background-color: #f9fafb;
    border-color: #0d9488;
    z-index: 1;

    .chevron-icon img {
      opacity: 1;
      transform: translateX(4px);
    }
  }

  &:active {
    background-color: #f0fdfa;
  }

  &.has-divider::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 72px;
    right: 0;
    height: 1px;
    background-color: #e5e7eb;

    @media (min-width: 768px) {
      display: none;
    }
  }
}

.organization-avatar-wrapper {
  flex-shrink: 0;
  margin-right: 12px;

  @media (min-width: 768px) {
    margin-right: 16px;
  }
}

.organization-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 2px solid #e5e7eb;
  transition: border-color 0.2s;

  .organization-list-item:hover & {
    border-color: #0d9488;
  }

  @media (min-width: 768px) {
    width: 44px;
    height: 44px;
  }

  @media (min-width: 1024px) {
    width: 48px;
    height: 48px;
  }
}

.organization-info {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
}

.organization-name {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1a1a1a;
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.organization-ein {
  font-size: 13px;
  line-height: 1.2;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.chevron-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    width: 20px;
    height: 20px;
    opacity: 0.4;
    transition: all 0.2s;

    @media (min-width: 768px) {
      width: 18px;
      height: 18px;
    }

    @media (min-width: 1024px) {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
